// Backend service interface
import { clerkService } from './clerkService.js';
import { get } from 'svelte/store';

import { documentMetadataStore } from '../stores/documentMetadataStore.js';

export class BackendService {
    constructor(baseUrl = import.meta.env?.VITE_API_URL || 
                         ((window.location.hostname.includes('vercel.app') || 
                           window.location.hostname === 'patcraft-demo.fearn.ai') ? 
                          'https://patcraft-production.up.railway.app' : 'http://localhost:5001')) {
        // Check if running in Electron and get backend URL
        if (window.electronAPI && window.electronAPI.getBackendUrl) {
            // Running in Electron - get backend URL from main process
            window.electronAPI.getBackendUrl().then(url => {
                this.baseUrl = url;
                console.log('BackendService: Running in Electron, backend URL:', url);
            });
        } else {
            // Running in browser - use provided URL or environment variable
            this.baseUrl = import.meta.env?.VITE_API_URL || baseUrl;
        }
        
        // Define available endpoints for feature detection
        this.availableEndpoints = new Set([
            'fetchTermChildTerms',
            'analyzeClaimsText',
            'regenerateSpec',
            'assignClaimsToLimitations'
        ]);

        // Request management for cancellation
        this._pendingRequests = new Map();
        this._requestCounter = 0;

        // Dedicated abort controller for term analysis
        this._termAnalysisController = null;

        // Dedicated abort controller for spec regeneration
        this._specRegenerationController = null;
    }

    getDocumentId() {
        const doc = get(documentMetadataStore);
        return doc?.id || 'default';
    }
    
    /**
     * Generic request method for making API calls with authentication
     * @param {string} endpoint - API endpoint (e.g., '/add-manual-term')
     * @param {Object} options - Fetch options (method, body, etc.)
     */
    async request(endpoint, options = {}) {
        const requestId = ++this._requestCounter;
        const controller = new AbortController();
        const url = `${this.baseUrl}${endpoint}`;
        
        try {
            this._pendingRequests.set(requestId, controller);
            
            // Get authentication token
            // SECURITY: Never log token details - tokens are sensitive credentials
            const token = await clerkService.getToken();
            
            const defaultOptions = {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    // Add Authorization header if token is available
                    ...(token && { 'Authorization': `Bearer ${token}` })
                },
                signal: controller.signal,
            };
            
            const config = { ...defaultOptions, ...options };
            
            // Merge headers if provided in options
            if (options.headers) {
                config.headers = { ...defaultOptions.headers, ...options.headers };
            }
            
            const response = await fetch(url, config);
            
            if (!response.ok) {
                // Try to get error message from response body
                let errorMessage = `HTTP error! status: ${response.status}`;
                try {
                    const errorData = await response.json();
                    if (errorData.error) {
                        errorMessage = errorData.error;
                    }
                } catch (e) {
                    // If response isn't JSON, use default message
                }
                throw new Error(errorMessage);
            }
            
            return response;
        } catch (error) {
            if (error.name === 'AbortError') {
                console.log(`Request ${requestId} was cancelled`);
                return null;
            }
            console.error(`Error with ${options.method || 'GET'} ${endpoint}:`, error);
            throw error;
        } finally {
            this._pendingRequests.delete(requestId);
        }
    }
    
    cancelAllRequests() {
        for (const [id, controller] of this._pendingRequests) {
            console.log(`Cancelling request ${id}`);
            controller.abort();
        }
        this._pendingRequests.clear();
    }

    /**
     * Stop the ongoing term analysis if one is running
     * @returns {boolean} - Whether a term analysis was stopped
     */
    stopTermAnalysis() {
        if (this._termAnalysisController) {
            console.log('🛑 Stopping term analysis...');
            this._termAnalysisController.abort();
            this._termAnalysisController = null;
            return true;
        }
        return false;
    }

    stopSpecRegeneration() {
        if (this._specRegenerationController) {
            console.log('🛑 Stopping spec regeneration...');
            this._specRegenerationController.abort();
            this._specRegenerationController = null;
            return true;
        }
        return false;
    }


     async clearBackendState() {
        console.log('🔄 BACKEND OPERATION STARTED: Clearing backend state');
        const startTime = Date.now();


        try {
            console.log('🧹 Clearing backend state');
            
            // Get authentication token
            const token = await clerkService.getToken();
            
            const response = await fetch(`${this.baseUrl}/clear-state`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    // Add Authorization header if token is available
                    ...(token && { 'Authorization': `Bearer ${token}` })
                },
                body: JSON.stringify({
                    document_id: this.getDocumentId()
                })
            });

            if (!response.ok) {
                // Try to get error message from response body
                let errorMessage = `HTTP error! status: ${response.status}`;
                try {
                    const errorData = await response.json();
                    if (errorData.error) {
                        errorMessage = errorData.error;
                    }
                } catch (e) {
                    // If response isn't JSON, use default message
                }
                throw new Error(errorMessage);
            }

            const result = await response.json();
            const elapsed = Date.now() - startTime;
            console.log(`✅ BACKEND OPERATION COMPLETED: State cleared in ${elapsed}ms`);


            return result;
        } catch (error) {
            console.error('❌ Error clearing backend state:', error);
            // Don't throw - this is not critical for app functionality
            return null;
        }
    }
    
    /**
     * Check if an endpoint is available
     * @param {string} endpoint - Name of the endpoint to check
     * @returns {boolean} - Whether the endpoint is available
     */
    hasEndpoint(endpoint) {
        return this.availableEndpoints.has(endpoint);
    }

    // Claims analysis - always uses streaming
    async analyzeClaimsText(text, forceHeavy = false, options = {}) {
        console.log('🔄 BACKEND OPERATION STARTED: Analyzing claims text');
        console.log(`   - Text length: ${text.length} characters`);
        console.log(`   - Force heavy refresh: ${forceHeavy}`);
        console.log(`   - Options:`, options);
        const startTime = Date.now();
        
        // Check if we should use the debug non-streaming endpoint
        const useDebugEndpoint = options.useDebugNonStreaming || false;
        
        if (useDebugEndpoint) {
            console.log('📍 Using non-streaming debug endpoint for comparison');
            return this.analyzeClaimsTextDebug(text, forceHeavy, options);
        }
        
        // Validate that onProgress callback is provided for streaming
        if (!options.onProgress || typeof options.onProgress !== 'function') {
            throw new Error('onProgress callback is required for streaming response');
        }
        
        console.log(`[MANUAL_REFRESH] Backend request config:`, {
            hasOnProgress: true,
            forceHeavy,
            clearBackendState: options.clearBackendState
        });
        
        let longRequestTimer;
        try {
            // Create abort controller for this analysis
            this._termAnalysisController = new AbortController();

            console.log(`   - Sending request to: ${this.baseUrl}/analyze-claims`);
            const requestBody = {
                text,
                force_heavy: forceHeavy,
                clear_backend_state: options.clearBackendState || false,
                manual_terms: options.manualTerms || [],
                document_id: options.document_id || this.getDocumentId(),
                is_provisional: options.is_provisional || false
            };
            console.log(`   - Request body:`, {
                textLength: text.length,
                force_heavy: requestBody.force_heavy,
                clear_backend_state: requestBody.clear_backend_state,
                manual_terms: requestBody.manual_terms,
                is_provisional: requestBody.is_provisional
            });
            console.log(`   - ACTUAL force_heavy value being sent:`, requestBody.force_heavy);

            // Get authentication token
            // SECURITY: Never log token details - tokens are sensitive credentials
            const token = await clerkService.getToken();
            console.log(`   - Auth token available: ${!!token}`);

            // Add a timer to log if the request is taking too long
            longRequestTimer = setTimeout(() => {
                console.log('   - Request is taking longer than 5 seconds...');
                longRequestTimer = setTimeout(() => {
                    console.log('   - Request is taking longer than 10 seconds...');
                }, 5000);
            }, 5000);

            const response = await fetch(`${this.baseUrl}/analyze-claims`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    // Add Authorization header if token is available
                    ...(token && { 'Authorization': `Bearer ${token}` })
                },
                body: JSON.stringify(requestBody),
                signal: this._termAnalysisController.signal,
            });
            
            clearTimeout(longRequestTimer);
            
            console.log(`   - Response received, status: ${response.status}`);
            
            if (!response.ok) {
                console.error('BackendService: Response not OK:', response.status);
                const errorText = await response.text();
                console.error('BackendService: Error details:', errorText);
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const contentType = response.headers.get('content-type');
            console.log(`   - Response content-type: ${contentType}`);
            console.log(`   - Is NDJSON: ${contentType?.includes('application/x-ndjson')}`);
            console.log(`   - Is JSON: ${contentType?.includes('application/json')}`);
            
            // Check if backend is in debug mode (returns JSON instead of streaming)
            if (contentType?.includes('application/json')) {
                console.log('   - Backend is in DEBUG MODE, processing non-streaming JSON response');
                const result = await response.json();
                
                // Check if this is a blocked response due to validation errors
                if (result.refresh_type === 'blocked') {
                    console.log('   - Backend returned BLOCKED due to validation errors:', result.validation_errors);
                }
                
                // Convert non-streaming result to match expected format
                const totalClaims = Object.keys(result.claims || {}).length;
                const progressData = {
                    term_counts: result.term_counts || {},
                    claims: result.claims || {},
                    claim_refs: result.corrected_claim_refs || result.claim_refs || {},
                    refresh_type: result.refresh_type,
                    state_summary: result.state_summary,
                    discovered_terms: Object.keys(result.term_counts || {}).length,
                    total_claims: totalClaims,
                    claims_processed: totalClaims,
                    validation_errors: result.validation_errors || null,
                    isComplete: true
                };
                
                // Call onProgress with the complete data
                if (options.onProgress) {
                    options.onProgress(progressData);
                }
                
                const elapsed = Date.now() - startTime;
                console.log(`✅ BACKEND OPERATION COMPLETED: Claims analysis (debug mode) in ${elapsed}ms`);
                console.log(`   - Terms found: ${progressData.discovered_terms}`);
                
                return progressData;
            }

            // If not JSON, process as streaming response
            console.log('   - Processing as streaming response (NDJSON)');
            const result = await this.processStreamingAnalysis(response, options.onProgress, startTime);
            // Clean up after successful streaming
            this._termAnalysisController = null;
            return result;
        } catch (error) {
            const elapsed = Date.now() - startTime;

            // Handle abort specifically
            if (error.name === 'AbortError') {
                console.log('✋ BACKEND OPERATION CANCELLED: Claims analysis stopped by user');
                this._termAnalysisController = null;
                // Clear the timer if error occurs
                if (longRequestTimer) {
                    clearTimeout(longRequestTimer);
                }
                // Return null to indicate cancellation instead of throwing
                return null;
            }

            console.error(`❌ BACKEND OPERATION FAILED: Claims analysis after ${elapsed}ms`);
            console.error('BackendService: Error details:', {
                message: error.message,
                stack: error.stack,
                error: error
            });
            // Clear the timer if error occurs
            if (longRequestTimer) {
                clearTimeout(longRequestTimer);
            }
            this._termAnalysisController = null;
            throw error;
        }
    }
    
    // Process streaming claims analysis response
    async processStreamingAnalysis(response, onProgress, startTime) {
        console.log('[MANUAL_REFRESH] processStreamingAnalysis started');
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        
        // Track progress data
        const progressData = {
            claims: {},
            claim_refs: {},
            corrected_claim_refs: null,  // Will be set from streaming 'complete' event
            term_counts: {},
            refresh_type: null,
            state_summary: null,
            discovered_terms: 0,
            total_parent_terms: 0,
            total_claims: 0,
            claims_processed: 0,
            isComplete: false
        };
        
        try {
            let chunkCount = 0;
            let streamContinue = true;
            while (streamContinue) {
                // Check if analysis was cancelled before reading next chunk
                if (this._termAnalysisController === null) {
                    console.log('[BackendService] Stream cancelled by user, stopping read loop');
                    reader.cancel();
                    return null; // Return null instead of throwing error
                }

                const { done, value } = await reader.read();

                if (done) {
                    streamContinue = false;
                    console.log('[BackendService] Stream reading done');
                    // Process any remaining data in buffer
                    if (buffer.trim()) {
                        try {
                            const event = JSON.parse(buffer);
                            this.processAnalysisStreamEvent(event, progressData, onProgress);
                        } catch (e) {
                            console.warn('BackendService: Error parsing final buffer:', e);
                        }
                    }
                    break;
                }
                
                // Decode chunk and add to buffer
                const chunk = decoder.decode(value, { stream: true });
                buffer += chunk;
                chunkCount++;
                
                console.log(`[MANUAL_REFRESH] Received chunk ${chunkCount}, size: ${chunk.length}, buffer size: ${buffer.length}`);
                
                // Process complete lines
                const lines = buffer.split('\n');
                buffer = lines.pop() || ''; // Keep incomplete line in buffer
                
                console.log(`[MANUAL_REFRESH] Processing ${lines.length} lines from chunk`);
                
                for (const line of lines) {
                    if (line.trim()) {
                        try {
                            const event = JSON.parse(line);
                            console.log(`[MANUAL_REFRESH] Processing event: ${event.type}`);
                            this.processAnalysisStreamEvent(event, progressData, onProgress);
                        } catch (e) {
                            console.warn('[MANUAL_REFRESH] Error parsing stream event:', e, line);
                        }
                    }
                }
            }
            
            const elapsed = Date.now() - startTime;
            console.log(`[MANUAL_REFRESH] Stream processing completed in ${elapsed}ms`);
            console.log(`[MANUAL_REFRESH] Final progress data:`, {
                discovered_terms: progressData.discovered_terms,
                claims_processed: progressData.claims_processed,
                total_claims: progressData.total_claims,
                term_counts_length: Object.keys(progressData.term_counts).length
            });
            
            progressData.isComplete = true;
            console.log('[MANUAL_REFRESH] Calling final onProgress callback');
            onProgress(progressData);
            
            return progressData;

        } catch (error) {
            // Handle abort gracefully
            if (error.name === 'AbortError') {
                console.log('BackendService: Streaming analysis cancelled by user');
                return null;
            }
            console.error('BackendService: Error during streaming analysis:', error);
            throw error;
        }
    }
    
    // Process individual stream events for claims analysis
    processAnalysisStreamEvent(event, progressData, onProgress) {
        const { type } = event;
        
        switch (type) {
            case 'init':
                // Initialize analysis
                progressData.refresh_type = event.refresh_type;
                progressData.total_parent_terms = event.total_parent_terms || 0;
                progressData.total_claims = event.total_claims || 0;
                progressData.claims = event.claims || {};
                progressData.claim_refs = event.claim_refs || {};
                progressData.claims_processed = 0;
                progressData.validation_errors = event.validation_errors || null;
                console.log(`[MANUAL_REFRESH] Starting ${event.refresh_type} refresh for ${event.total_claims} claims`);
                if (progressData.validation_errors) {
                    console.log(`[MANUAL_REFRESH] Validation errors present:`, progressData.validation_errors);
                }
                // Call onProgress to update UI with initial values
                onProgress(progressData);
                break;
                
            case 'claim_processed':
                // Individual claim has been processed
                progressData.claims_processed = event.claims_processed;
                console.log(`[MANUAL_REFRESH] Processed claim ${event.claim_num} (${event.claims_processed}/${event.total_claims}) - ${event.terms_found} terms found`);
                onProgress(progressData);
                break;
                
            case 'parent_term': {
                // New parent term discovered
                const { parent_term, claim_num, start_char, end_char } = event;
                progressData.term_counts[parent_term] = {
                    parent_term,
                    claim_num,
                    start_char,
                    end_char,
                    analyzed_for_children: false,
                    children: {}
                };
                progressData.discovered_terms++;
                console.log(`[MANUAL_REFRESH] Discovered parent term: "${parent_term}" (total: ${progressData.discovered_terms})`);
                onProgress(progressData);
                break;
            }
                
            case 'phase_change':
                // Analysis phase change
                console.log(`[MANUAL_REFRESH] Phase: ${event.phase} - ${event.message}`);
                onProgress(progressData);
                break;
                
            case 'child_terms_starting':
                // Child term generation starting for a parent
                console.log(`[MANUAL_REFRESH] Starting child terms for "${event.parent_term}" (${event.current_progress}/${event.total_parents})`);
                onProgress(progressData);
                break;
                
            case 'child_terms_generated':
                // Child term generation completed for a parent
                console.log(`[MANUAL_REFRESH] Generated ${event.child_count} child terms for "${event.parent_term}" (${event.current_progress}/${event.total_parents})`);
                onProgress(progressData);
                break;
                
            case 'child_terms_cached':
                // Using cached child terms for a parent
                console.log(`[MANUAL_REFRESH] Using ${event.child_count} cached child terms for "${event.parent_term}" (${event.current_progress}/${event.total_parents})`);
                onProgress(progressData);
                break;
                
            case 'child_terms': {
                // Child terms generated for a parent
                const { parent, children } = event;
                if (progressData.term_counts[parent]) {
                    progressData.term_counts[parent].analyzed_for_children = true;
                    
                    // Add each child term
                    for (const childData of children) {
                        progressData.term_counts[parent].children[childData.term] = childData;
                        progressData.discovered_terms++;
                    }
                    
                    console.log(`[MANUAL_REFRESH] Generated ${children.length} child terms for "${parent}"`);
                    onProgress(progressData);
                }
                break;
            }
                
            case 'term_counts_update':
                // Update term counts and references (light refresh)
                if (event.updated_terms) {
                    progressData.term_counts = event.updated_terms;
                    console.log('[MANUAL_REFRESH] Updated term counts');
                    onProgress(progressData);
                }
                break;
                
            case 'complete':
                // Analysis complete - merge in all the final data
                progressData.term_counts = event.term_counts || progressData.term_counts;
                progressData.claims = event.claims || progressData.claims;
                // Store corrected_claim_refs explicitly for claimsTermUpdateService
                progressData.corrected_claim_refs = event.corrected_claim_refs || null;
                // Also set claim_refs for backwards compatibility, preferring corrected version
                progressData.claim_refs = event.corrected_claim_refs || event.claim_refs || progressData.claim_refs;
                progressData.refresh_type = event.refresh_type || progressData.refresh_type;
                progressData.state_summary = event.state_summary;
                progressData.validation_errors = event.validation_errors || null;
                progressData.isComplete = true;
                console.log('[MANUAL_REFRESH] Analysis complete, corrected_claim_refs:', !!event.corrected_claim_refs);
                break;
                
            case 'hierarchy_corrections': {
                // Handle term reclassifications
                const { reclassifications } = event;
                if (reclassifications && reclassifications.length > 0) {
                    console.log(`[MANUAL_REFRESH] Processing ${reclassifications.length} term reclassifications`);
                    
                    // Remove reclassified terms from parent level in term_counts
                    reclassifications.forEach(reclass => {
                        if (progressData.term_counts[reclass.term]) {
                            console.log(`[MANUAL_REFRESH] Removing "${reclass.term}" from parent terms (now child of: ${reclass.new_parents.join(', ')})`);
                            delete progressData.term_counts[reclass.term];
                            progressData.discovered_terms--;
                        }
                    });
                    
                    onProgress(progressData);
                }
                break;
            }
                
            case 'error':
                // Handle errors
                console.error('[MANUAL_REFRESH] Stream error:', event.message);
                break;
                
            default:
                console.warn('[MANUAL_REFRESH] Unknown stream event type:', type);
        }
    }

    // Non-streaming debug version for comparison
    async analyzeClaimsTextDebug(text, forceHeavy = false, options = {}) {
        console.log('🔬 DEBUG: Using non-streaming analyze-claims-debug endpoint');
        const startTime = Date.now();
        
        try {
            // Get authentication token
            const token = await clerkService.getToken();
            
            const response = await fetch(`${this.baseUrl}/analyze-claims-debug`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    // Add Authorization header if token is available
                    ...(token && { 'Authorization': `Bearer ${token}` })
                },
                body: JSON.stringify({
                    text,
                    force_heavy: forceHeavy,
                    clear_backend_state: options.clearBackendState || false,
                    manual_terms: options.manualTerms || [],
                    document_id: options.document_id || this.getDocumentId(),
                    is_provisional: options.is_provisional || false
                })
            });
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
            }
            
            const result = await response.json();
            const elapsed = Date.now() - startTime;
            console.log(`🔬 DEBUG: Non-streaming analysis completed in ${elapsed}ms`);
            console.log(`🔬 DEBUG: Result structure:`, {
                type: result.type,
                refresh_type: result.refresh_type,
                term_counts: Object.keys(result.term_counts || {}).length,
                hierarchy_corrections: result.hierarchy_corrections?.length || 0
            });
            
            // Convert non-streaming result to match streaming format
            return {
                term_counts: result.term_counts,
                claims: result.claims,
                claim_refs: result.corrected_claim_refs || result.claim_refs,
                refresh_type: result.refresh_type,
                state_summary: result.state_summary,
                hierarchy_corrections: result.hierarchy_corrections
            };
            
        } catch (error) {
            console.error('🔬 DEBUG: Error during non-streaming analysis:', error);
            throw error;
        }
    }

    async regenerateSpecStream(claims, spec, contexts = [], options = {}) {
        console.log('🔄 BACKEND OPERATION STARTED: Regenerating specification (streaming)');
        console.log(`   - Claims length: ${claims.length} characters`);
        console.log(`   - Spec length: ${spec.length} characters`);
        console.log(`   - Contexts:`, contexts);
        console.log(`   - Invention disclosure: ${options.inventionDisclosure ? options.inventionDisclosure.length + ' chars' : 'none'}`);
        const startTime = Date.now();

        // Create and store abort controller for this spec regeneration
        this._specRegenerationController = new AbortController();

        // Use SSE endpoint with skeleton loader support
        if (options.onProgress) {
            try {
                const token = await clerkService.getToken();
                console.log(`   - Auth token available for spec regen: ${!!token}`);
                console.log(`   - Making request to: ${this.baseUrl}/regenerate-spec-stream`);
                const response = await fetch(`${this.baseUrl}/regenerate-spec-stream`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        // Add Authorization header if token is available
                        ...(token && { 'Authorization': `Bearer ${token}` })
                    },
                    body: JSON.stringify({
                        claims,
                        spec,
                        contexts,
                        invention_disclosure: options.inventionDisclosure || '',
                        title: options.title || ''
                    }),
                    signal: this._specRegenerationController.signal
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                // Read the SSE stream
                const reader = response.body.getReader();
                const decoder = new TextDecoder();
                let buffer = '';
                let finalResult = { results: {} };

                // Initialize result structure for each context
                for (const context of contexts) {
                    finalResult.results[context] = [];
                }

                let streamDone = false;
                while (!streamDone) {
                    const { done, value } = await reader.read();

                    if (done) {
                        streamDone = true;
                        break;
                    }

                    buffer += decoder.decode(value, { stream: true });

                    // Process complete SSE messages
                    const lines = buffer.split('\n');
                    buffer = lines[lines.length - 1]; // Keep incomplete line in buffer

                    for (let i = 0; i < lines.length - 1; i++) {
                        const line = lines[i].trim();

                        if (line.startsWith('data: ')) {
                            const jsonStr = line.slice(6); // Remove 'data: ' prefix

                            try {
                                const data = JSON.parse(jsonStr);
                                console.log('[DEBUG-SSE] Parsed SSE event:', data);

                                if (data.type === 'init') {
                                    // Handle init events for skeleton loaders
                                    console.log('[DEBUG-SSE] Init event detected:', data);
                                    options.onProgress(data);
                                } else if (data.type === 'progress') {
                                    // Build up the final result
                                    if (data.context === 'summary') {
                                        finalResult.results.summary.push({
                                            type: 'group',
                                            context: 'summary',
                                            claim_numbers: data.claim_numbers,
                                            content: data.content
                                        });
                                    } else if (data.context === 'embodiments') {
                                        finalResult.results.embodiments.push({
                                            type: 'content',
                                            context: 'embodiments',
                                            content: data.content
                                        });
                                    }

                                    // Call progress callback with current state
                                    options.onProgress(data);

                                } else if (data.type === 'content') {
                                    // Handle content type events (for embodiments)
                                    if (data.context === 'embodiments') {
                                        finalResult.results.embodiments.push({
                                            type: 'content',
                                            context: 'embodiments',
                                            content: data.content
                                        });
                                    }

                                    // Call progress callback 
                                    options.onProgress(data);

                                } else if (data.type === 'group_error') {
                                    // Add error to results
                                    finalResult.results.summary.push({
                                        type: 'group_error',
                                        context: 'summary',
                                        claim_numbers: data.claim_numbers,
                                        error: data.error,
                                        message: data.message
                                    });

                                    // Also call progress callback for real-time error display
                                    options.onProgress(data);

                                } else if (data.type === 'validation_error') {
                                    console.error('[SPEC-REGEN] Validation errors detected:', data.validation_errors);
                                    // Update the claims editor with validation errors
                                    if (window.updateClaimsValidationErrors) {
                                        window.updateClaimsValidationErrors(data.validation_errors);
                                    }
                                    
                                    // Call progress callback to handle in UI
                                    options.onProgress(data);
                                    
                                    // Don't continue processing if blocked
                                    return { 
                                        validation_errors: data.validation_errors,
                                        blocked: true,
                                        results: {}
                                    };
                                    
                                } else if (data.type === 'subcomponent-error') {
                                    // Handle sub-component error events (granular errors for detailed-description)
                                    console.log('[DEBUG-SSE] Sub-component error event:', data);
                                    options.onProgress(data);

                                } else if (data.type === 'subcomponent-progress') {
                                    // Handle sub-component progress events (granular progress for detailed-description)
                                    console.log('[DEBUG-SSE] Sub-component progress event:', data);
                                    options.onProgress(data);

                                } else if (data.type === 'error') {
                                    console.error('Streaming error:', data.message);
                                    if (data.context === 'embodiments') {
                                        finalResult.results.embodiments.push({
                                            type: 'error',
                                            context: 'embodiments',
                                            message: data.message
                                        });
                                    }

                                    // Also call progress callback for real-time error display
                                    options.onProgress(data);

                                } else if (data.type === 'complete') {
                                    console.log('Streaming complete');
                                }

                            } catch (parseError) {
                                console.error('Error parsing SSE data:', parseError, jsonStr);
                            }
                        }
                    }
                }

                const elapsed = Date.now() - startTime;
                console.log(`✅ BACKEND OPERATION COMPLETED: Specification regenerated (streaming) in ${elapsed}ms`);

                // Return the accumulated results in the expected format
                return {
                    type: 'regeneration_complete',
                    contexts: contexts,
                    results: finalResult.results
                };

            } catch (error) {
                const elapsed = Date.now() - startTime;

                // Handle abort gracefully
                if (error.name === 'AbortError') {
                    console.log('✋ BACKEND OPERATION CANCELLED: Spec regeneration stopped by user');
                    return null;
                }

                console.error(`❌ BACKEND OPERATION FAILED: Specification regeneration (streaming) after ${elapsed}ms`);
                console.error('BackendService: Error regenerating spec with streaming:', error);
                throw error;
            } finally {
                // Clean up the abort controller
                this._specRegenerationController = null;
            }
        }

        // Fallback to non-streaming if no progress callback
        throw new Error('Streaming regenerateSpec requires onProgress callback');
    }

    // Helper function to escape HTML content
    escapeHtml(content) {
        // Check if content is an object or a string
        if (typeof content === 'object' && content !== null) {
            // If it's an object, convert to string representation
            // Filter out null/undefined values and join all values with newlines
            const filteredValues = Object.values(content).filter(val => val != null);
            const contentStr = filteredValues.join('\n\n');
            return this.escapeHtml(contentStr);
        }
        
        // Handle case where content might be undefined or null
        if (content == null || content === '') return '';
        
        // Ensure content is a string
        const strContent = String(content);
        
        // Escape HTML special characters
        return strContent
            .replace(/&/g, '&amp;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');
    }

    // Spec regeneration (streaming)
    async regenerateSpec(claims, spec, contexts = [], options = {}) {
        try {
            // Create an object to track progress and collect results
            const progressData = {
                contexts: {},
                isComplete: false,
                formattedContent: '',
                
                // Initialize empty collections for each context
                init() {
                    for (const context of contexts) {
                        this.contexts[context] = {
                            isInitialized: false,
                            isComplete: false,
                            claims: {},
                            content: ''
                        };
                    }
                    return this;
                },
                
                // Format the collected content into HTML
                formatContent() {
                    let formatted = '';
                    
                    for (const context of contexts) {
                        const contextData = this.contexts[context];
                        
                        if (context === 'summary') {
                            // Check if we have grouped claims
                            if (contextData.groups && contextData.groups.length > 0) {
                                // Sort groups by index to ensure correct order
                                const sortedGroups = [...contextData.groups].sort((a, b) => a.groupIndex - b.groupIndex);
                                
                                // For summary with groups, create paragraphs for each group
                                sortedGroups.forEach((group) => {
                                    if (group.content && group.content.trim()) {
                                        const escapedContent = this.escapeHtml(group.content);
                                        
                                        // Split by double newlines
                                        const paragraphs = escapedContent.split('\n\n');
                                        
                                        // Create a separate paragraph for each, with claim numbers as data attribute
                                        paragraphs.forEach((paragraph) => {
                                            if (paragraph.trim()) {
                                                formatted += `<p class="editor-paragraph" data-block-type="regenerative-support" data-context="${context}" data-claim-group="${group.claimNumbers.join(',')}">${paragraph}</p>`;
                                            }
                                        });
                                    }
                                });
                            } else if (Object.keys(contextData.claims).length > 0) {
                                // Fallback to individual claims format for backward compatibility
                                Object.entries(contextData.claims).forEach(([claim_num, content]) => {
                                    if (content && content.trim()) {
                                        const escapedContent = this.escapeHtml(content);
                                        
                                        // Split by double newlines
                                        const paragraphs = escapedContent.split('\n\n');
                                        
                                        // Create a separate paragraph for each
                                        paragraphs.forEach((paragraph) => {
                                            if (paragraph.trim()) {
                                                formatted += `<p class="editor-paragraph" data-block-type="regenerative-support" data-context="${context}" data-claim="${claim_num}">${paragraph}</p>`;
                                            }
                                        });
                                    }
                                });
                            }
                        } else if (contextData.content) {
                            // For other contexts, we have a single content string
                            const escapedContent = this.escapeHtml(contextData.content);
                            
                            // Split the content and create paragraphs
                            const paragraphs = escapedContent.split('\n\n');
                            
                            paragraphs.forEach((paragraph) => {
                                if (paragraph.trim()) {
                                    formatted += `<p class="editor-paragraph" data-block-type="regenerative-support" data-context="${context}">${paragraph}</p>`;
                                }
                            });
                        }
                    }
                    
                    this.formattedContent = formatted;
                    return formatted;
                },
                
                // Bind escapeHtml to use in formatContent
                escapeHtml: this.escapeHtml.bind(this)
            }.init();
            
            // Callback function for progress updates
            const onProgress = options.onProgress || (() => {});
            
            // Get authentication token
            const token = await clerkService.getToken();
            
            // Start the fetch request with streaming response
            console.log('[DEBUG-STREAM] About to make request to:', `${this.baseUrl}/regenerate-spec`);
            console.log('[DEBUG-STREAM] Request body:', { 
                claims: claims.substring(0, 100) + '...', 
                spec: spec.substring(0, 100) + '...', 
                contexts,
                use_streaming: true,
                document_id: this.getDocumentId()
            });
            
            const response = await fetch(`${this.baseUrl}/regenerate-spec`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    // Add Authorization header if token is available
                    ...(token && { 'Authorization': `Bearer ${token}` })
                },
                body: JSON.stringify({ 
                    claims, 
                    spec, 
                    contexts,
                    use_streaming: true,
                    document_id: this.getDocumentId()
                }),
            });
            
            console.log('[DEBUG-STREAM] Response received:', response.status, response.statusText);
            console.log('[DEBUG-STREAM] Response headers:', Array.from(response.headers.entries()));
            
            if (!response.ok) {
                // Try to get error message from response body
                let errorMessage = `HTTP error! status: ${response.status}`;
                try {
                    const errorData = await response.json();
                    console.error('[DEBUG-STREAM] Error response:', errorData);
                    if (errorData.error) {
                        errorMessage = errorData.error;
                    }
                } catch (e) {
                    console.error('[DEBUG-STREAM] Could not parse error response:', e);
                    // If response isn't JSON, use default message
                }
                throw new Error(errorMessage);
            }
            
            // Get a reader to read the stream
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            
            console.log('[DEBUG-STREAM] Starting to read stream...');
            
            // Process each chunk as it arrives
            let streamContinue = true;
            while (streamContinue) {
                const { done, value } = await reader.read();
                
                if (done) {
                    streamContinue = false;
                    // Handle any remaining data in the buffer
                    if (buffer.trim()) {
                        try {
                            const event = JSON.parse(buffer);
                            this.processStreamEvent(event, progressData, onProgress);
                        } catch (e) {
                            console.warn('BackendService: Error parsing final buffer data:', e);
                        }
                    }
                    break;
                }
                
                // Decode the chunk and add to buffer
                const chunk = decoder.decode(value, { stream: true });
                console.log('[DEBUG-STREAM] Received chunk:', chunk.length, 'chars, content:', JSON.stringify(chunk.substring(0, 200)));
                buffer += chunk;
                
                // Process complete lines in the buffer
                const lines = buffer.split('\n');
                buffer = lines.pop() || ''; // Keep the last incomplete line in the buffer
                console.log('[DEBUG-STREAM] Processing', lines.length, 'lines from buffer');
                
                // Process each complete line as a JSON event
                for (const line of lines) {
                    if (line.trim()) {
                        console.log('[DEBUG-STREAM] Raw line from backend:', line);
                        try {
                            const event = JSON.parse(line);
                            console.log('[DEBUG-STREAM] Parsed JSON event:', event);
                            this.processStreamEvent(event, progressData, onProgress);
                        } catch (e) {
                            console.warn('BackendService: Error parsing stream event:', e, line);
                        }
                    }
                }
            }
            
            // Call onProgress one last time with final state
            progressData.isComplete = true;
            onProgress(progressData);
            
            return { content: progressData.formattedContent };
            
        } catch (error) {
            console.error('BackendService: Error during streaming regeneration:', error);
            throw error;
        }
    }
    
    // Helper to process stream events
    processStreamEvent(event, progressData, onProgress) {
        const { type, context } = event;
        console.log('[DEBUG-BACKEND] Processing stream event type:', type, 'context:', context, 'full event:', event);
        
        switch (type) {
            case 'init':
                console.log('[DEBUG-BACKEND] ✅ INIT EVENT DETECTED:', event);
                // Initialize the context
                if (context && progressData.contexts[context]) {
                    progressData.contexts[context].isInitialized = true;
                    
                    // For summary, track total claims
                    if (context === 'summary' && event.total_claims) {
                        progressData.contexts[context].totalClaims = event.total_claims;
                    }
                }
                // Pass the init event to onProgress for skeleton creation
                console.log('[DEBUG-BACKEND] ✅ CALLING ONPROGRESS WITH INIT EVENT:', event);
                onProgress(event);
                break;
                
            case 'claim':
                // Handle individual claim result (for summary context)
                if (context === 'summary' && event.claim_num && event.content) {
                    // Check if we already have content for this claim
                    progressData.contexts[context].claims[event.claim_num] = event.content;
                    
                    // Format the current content and notify progress
                    progressData.formatContent();
                    onProgress(progressData);
                }
                break;
                
            case 'group':
                // Handle grouped claim results (for summary context)
                if (context === 'summary' && event.claim_numbers && event.content) {
                    // Initialize groups array if not present
                    if (!progressData.contexts[context].groups) {
                        progressData.contexts[context].groups = [];
                    }
                    
                    // Store the group data
                    progressData.contexts[context].groups.push({
                        claimNumbers: event.claim_numbers,
                        content: event.content,
                        groupIndex: event.group_index
                    });
                    
                    // Format the current content and notify progress
                    progressData.formatContent();
                    // Pass the event with type 'progress' for the onProgress handler
                    onProgress({ 
                        type: 'progress', 
                        context: context, 
                        claim_numbers: event.claim_numbers, 
                        content: event.content 
                    });
                }
                break;
                
            case 'content':
                // Handle non-claim specific content (detailed_description, embodiments)
                if (context && progressData.contexts[context] && event.content) {
                    progressData.contexts[context].content = event.content;
                    
                    // Format the current content and notify progress
                    progressData.formatContent();
                    // Pass the event for embodiments content
                    if (context === 'embodiments') {
                        onProgress({
                            type: 'content',
                            context: context,
                            content: event.content
                        });
                    } else {
                        onProgress(progressData);
                    }
                }
                break;
                
            case 'complete':
                // Mark a context as complete
                if (context && progressData.contexts[context]) {
                    progressData.contexts[context].isComplete = true;
                    
                    // Check if all contexts are complete
                    const allComplete = Object.values(progressData.contexts)
                        .every(ctx => ctx.isComplete);
                    
                    if (allComplete) {
                        progressData.isComplete = true;
                    }
                    
                    // Notify progress
                    onProgress(progressData);
                }
                break;
                
            case 'error':
                // Handle error events
                console.error('BackendService: Stream error for context', context, event.message);
                
                // For claim-specific errors, store error message
                if (context === 'summary' && event.claim_num) {
                    progressData.contexts[context].claims[event.claim_num] = 
                        `Error: ${event.message || 'Unknown error'}`;
                } else if (context) {
                    progressData.contexts[context].error = event.message || 'Unknown error';
                }
                
                // Notify progress
                progressData.formatContent();
                onProgress(progressData);
                break;
                
            case 'final_complete':
                // Final completion event
                progressData.isComplete = true;
                progressData.formatContent();
                onProgress(progressData);
                break;
                
            default:
                console.warn('[MANUAL_REFRESH] Unknown stream event type:', type);
        }
    }

    // Assign claims to their best matching elements
    async assignClaimsToLimitations(limitations, claims) {
        try {
            // Get authentication token
            const token = await clerkService.getToken();
            
            const response = await fetch(`${this.baseUrl}/assign-claims`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    // Add Authorization header if token is available
                    ...(token && { 'Authorization': `Bearer ${token}` })
                },
                body: JSON.stringify({ 
                    elements: limitations, // Backend still expects 'elements' key
                    claims,
                    document_id: this.getDocumentId()
                }),
            });

            if (!response.ok) {
                console.error('BackendService: Response not OK:', response.status);
                const errorText = await response.text();
                console.error('BackendService: Error details:', errorText);
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            // Return the full response which may include detailed_description in debug mode
            return data;
        } catch (error) {
            console.error('BackendService: Error assigning claims to limitations:', {
                message: error.message,
                stack: error.stack
            });
            throw error;
        }
    }

    
    // Fetch term child terms
    async fetchTermChildTerms(term) {
        console.log(`🔄 BACKEND OPERATION STARTED: Fetching child terms for "${term}"`);
        const startTime = Date.now();
        
        try {
            // Get authentication token
            const token = await clerkService.getToken();
            
            const response = await fetch(`${this.baseUrl}/child-terms`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    // Add Authorization header if token is available
                    ...(token && { 'Authorization': `Bearer ${token}` })
                },
                body: JSON.stringify({ 
                    term,
                    document_id: this.getDocumentId()
                }),
            });
            
            if (!response.ok) {
                console.error('BackendService: Response not OK:', response.status);
                const errorText = await response.text();
                console.error('BackendService: Error details:', errorText);
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            const elapsed = Date.now() - startTime;
            console.log(`✅ BACKEND OPERATION COMPLETED: Child terms for "${term}" in ${elapsed}ms`);
            console.log(`   - Child terms found: ${data.child_terms ? data.child_terms.length : 0}`);
            
            return data;
        } catch (error) {
            const elapsed = Date.now() - startTime;
            console.error(`❌ BACKEND OPERATION FAILED: Child terms for "${term}" after ${elapsed}ms`);
            console.error('BackendService: Error fetching term child terms:', {
                message: error.message,
                stack: error.stack
            });
            // Re-throw the error - no fallback
            throw error;
        }
    }

    /**
     * Generate literal support for multiple claims (streaming)
     * @param {Object} claims - Dictionary mapping claim number to claim text
     * @param {Object} options - Optional configuration
     * @returns {Promise<Object>} - Promise that resolves with the complete literal support data
     */
    async generateLiteralSupportBatch(claims, options = {}) {
        console.log('🔄 BACKEND OPERATION STARTED: Generating literal support for claims');
        console.log(`   - Number of claims: ${Object.keys(claims).length}`);
        const startTime = Date.now();
        
        try {
            // Create an object to track progress
            const progressData = {
                literalSupport: {},
                paragraphs: [], // Store paragraphs in group order
                totalClaims: 0,
                totalGroups: 0,
                processedGroups: 0,
                isComplete: false,
                errors: {},
                claimGroups: [] // Add claim groups for paragraphing
            };
            
            // Get authentication token
            const token = await clerkService.getToken();
            
            const response = await fetch(`${this.baseUrl}/prepare-claims-for-detailed-description`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    // Add Authorization header if token is available
                    ...(token && { 'Authorization': `Bearer ${token}` })
                },
                body: JSON.stringify({ 
                    claims,
                    context: options.context || 'brief_summary',  // Pass context from options
                    document_id: this.getDocumentId()
                }),
            });
            
            if (!response.ok) {
                console.error('BackendService: Response not OK:', response.status);
                const errorText = await response.text();
                console.error('BackendService: Error details:', errorText);
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            // Handle streaming response
            const reader = response.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            
            let done = false;
            while (!done) {
                const result = await reader.read();
                done = result.done;
                
                if (done) break;
                
                const value = result.value;
                
                buffer += decoder.decode(value, { stream: true });
                
                // Process complete lines
                const lines = buffer.split('\n');
                buffer = lines.pop() || ''; // Keep the incomplete line in buffer
                
                for (const line of lines) {
                    if (!line.trim()) continue;
                    
                    try {
                        const data = JSON.parse(line);
                        
                        switch (data.type) {
                            case 'init':
                                progressData.totalClaims = data.total_claims;
                                progressData.totalGroups = data.total_groups;
                                progressData.claimGroups = data.claim_groups || [];
                                // Initialize paragraphs array
                                progressData.paragraphs = new Array(data.total_groups);
                                console.log(`BackendService: Starting literal support generation for ${data.total_claims} claims in ${data.total_groups} groups`);
                                console.log(`BackendService: Claim groups:`, progressData.claimGroups);
                                if (options.onProgress) {
                                    options.onProgress({
                                        type: 'init',
                                        totalClaims: data.total_claims,
                                        totalGroups: data.total_groups,
                                        claimGroups: data.claim_groups
                                    });
                                }
                                break;
                                
                            case 'group':
                                // Store paragraph content by group index
                                progressData.paragraphs[data.group_index] = data.content;
                                progressData.processedGroups++;
                                
                                // Also store individual claims for backward compatibility
                                data.claim_numbers.forEach(claimNum => {
                                    progressData.literalSupport[claimNum] = data.content;
                                });
                                
                                console.log(`BackendService: Generated literal support for group ${data.group_index + 1} (claims ${data.claim_numbers.join(', ')}) - ${progressData.processedGroups}/${progressData.totalGroups}`);
                                if (options.onProgress) {
                                    options.onProgress({
                                        type: 'group',
                                        groupIndex: data.group_index,
                                        claimNumbers: data.claim_numbers,
                                        content: data.content,
                                        processedGroups: progressData.processedGroups,
                                        totalGroups: progressData.totalGroups,
                                        progress: progressData.processedGroups / progressData.totalGroups,
                                        errors: data.errors || []
                                    });
                                }
                                break;
                                
                            case 'group_error':
                                console.error(`BackendService: Error processing claim group ${data.group_index}:`, data.message);
                                progressData.errors[`group_${data.group_index}`] = data.message;
                                progressData.processedGroups++;
                                
                                if (options.onProgress) {
                                    options.onProgress({
                                        type: 'group_error',
                                        groupIndex: data.group_index,
                                        claimNumbers: data.claim_numbers,
                                        message: data.message,
                                        processedGroups: progressData.processedGroups,
                                        totalGroups: progressData.totalGroups
                                    });
                                }
                                break;
                                
                            case 'complete':
                                progressData.isComplete = true;
                                console.log('BackendService: Literal support generation complete');
                                if (options.onProgress) {
                                    options.onProgress({
                                        type: 'complete',
                                        processedGroups: progressData.processedGroups,
                                        totalGroups: progressData.totalGroups,
                                        processedClaims: progressData.totalClaims,
                                        totalClaims: progressData.totalClaims
                                    });
                                }
                                break;
                        }
                    } catch (parseError) {
                        console.error('BackendService: Error parsing streaming response line:', line, parseError);
                    }
                }
            }
            
            const elapsed = Date.now() - startTime;
            console.log(`✅ BACKEND OPERATION COMPLETED: Literal support generation in ${elapsed}ms`);
            console.log(`   - Successfully processed: ${progressData.processedGroups}/${progressData.totalGroups} groups`);
            console.log(`   - Paragraph count: ${progressData.paragraphs.filter(p => p).length}`);
            console.log(`   - Errors: ${Object.keys(progressData.errors).length}`);
            
            return progressData;
            
        } catch (error) {
            const elapsed = Date.now() - startTime;
            console.error(`❌ BACKEND OPERATION FAILED: Literal support generation after ${elapsed}ms`);
            console.error('BackendService: Error:', error);
            throw error;
        }
    }
    
    /**
     * Delete a term from the backend state
     * @param {string} termName - The term to delete
     * @param {string} claimsText - Current claims text (needed to rebuild structure)
     * @returns {Promise<Object>} - Promise that resolves with updated term structure
     */
    async deleteTerm(termName, claimsText) {
        console.log(`🔄 BACKEND OPERATION STARTED: Deleting term "${termName}"`);
        const startTime = Date.now();
        
        try {
            // Get authentication token
            const token = await clerkService.getToken();
            
            const response = await fetch(`${this.baseUrl}/term/${encodeURIComponent(termName)}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    // Add Authorization header if token is available
                    ...(token && { 'Authorization': `Bearer ${token}` })
                },
                body: JSON.stringify({ 
                    claims_text: claimsText,
                    document_id: this.getDocumentId()
                }),
            });
            
            if (!response.ok) {
                const errorText = await response.text();
                console.error('BackendService: Error deleting term:', errorText);
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            const elapsed = Date.now() - startTime;
            console.log(`✅ BACKEND OPERATION COMPLETED: Term "${termName}" deleted in ${elapsed}ms`);
            console.log(`   - Updated terms: ${data.term_counts ? Object.keys(data.term_counts).length : 0}`);
            
            return data;
        } catch (error) {
            const elapsed = Date.now() - startTime;
            console.error(`❌ BACKEND OPERATION FAILED: Delete term "${termName}" after ${elapsed}ms`);
            console.error('BackendService: Error:', error);
            throw error;
        }
    }
    
    /**
     * Edit a term name in the backend state
     * @param {string} oldName - Current term name
     * @param {string} newName - New term name
     * @param {string} claimsText - Current claims text (needed to rebuild structure)
     * @returns {Promise<Object>} - Promise that resolves with updated term structure
     */
    async editTerm(oldName, newName, claimsText) {
        console.log(`🔄 BACKEND OPERATION STARTED: Editing term "${oldName}" -> "${newName}"`);
        const startTime = Date.now();
        
        try {
            // Get authentication token
            const token = await clerkService.getToken();
            
            const response = await fetch(`${this.baseUrl}/term/${encodeURIComponent(oldName)}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    // Add Authorization header if token is available
                    ...(token && { 'Authorization': `Bearer ${token}` })
                },
                body: JSON.stringify({ 
                    new_name: newName,
                    claims_text: claimsText,
                    document_id: this.getDocumentId()
                }),
            });
            
            if (!response.ok) {
                const errorText = await response.text();
                console.error('BackendService: Error editing term:', errorText);
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const data = await response.json();
            const elapsed = Date.now() - startTime;
            console.log(`✅ BACKEND OPERATION COMPLETED: Term "${oldName}" -> "${newName}" in ${elapsed}ms`);
            console.log(`   - Updated terms: ${data.term_counts ? Object.keys(data.term_counts).length : 0}`);
            
            return data;
        } catch (error) {
            const elapsed = Date.now() - startTime;
            console.error(`❌ BACKEND OPERATION FAILED: Edit term "${oldName}" -> "${newName}" after ${elapsed}ms`);
            console.error('BackendService: Error:', error);
            throw error;
        }
    }
    
    // New persistent user modification methods
    
    /**
     * Add manual term that persists through refreshes
     * @param {Object} params - Parameters {term_text, claims_text, document_id}
     */
    async addManualTerm(params) {
        console.log(`🔄 BACKEND OPERATION STARTED: Adding manual term "${params.term_text}"`);
        const startTime = Date.now();
        
        try {
            const response = await this.request('/add-manual-term', {
                method: 'POST',
                body: JSON.stringify({
                    ...params,
                    document_id: this.getDocumentId()
                })
            });

            if (!response) return null; // Request was cancelled
            
            const result = await response.json();
            
            const elapsed = Date.now() - startTime;
            console.log(`✅ BACKEND OPERATION COMPLETED: Manual term "${params.term_text}" added in ${elapsed}ms`);
            console.log(`   - Terms found: ${result.term_counts ? Object.keys(result.term_counts).length : 0}`);
            
            return result;
        } catch (error) {
            const elapsed = Date.now() - startTime;
            console.error(`❌ BACKEND OPERATION FAILED: Add manual term "${params.term_text}" after ${elapsed}ms`);
            throw error;
        }
    }
    
    /**
     * Delete term by text pattern - persists through refreshes
     * @param {Object} params - Parameters {term_text, claims_text, claim_num?, document_id, current_term_counts?}
     */
    async deleteTermPattern(params) {
        console.log(`🔄 BACKEND OPERATION STARTED: Deleting term pattern "${params.term_text}"`);
        const startTime = Date.now();
        
        try {
            const response = await this.request('/delete-term-pattern', {
                method: 'DELETE',
                body: JSON.stringify({
                    ...params,
                    document_id: this.getDocumentId()
                })
            });

            if (!response) return null; // Request was cancelled
            
            const result = await response.json();
            
            const elapsed = Date.now() - startTime;
            console.log(`✅ BACKEND OPERATION COMPLETED: Term pattern "${params.term_text}" deleted in ${elapsed}ms`);
            console.log(`   - Terms found: ${result.term_counts ? Object.keys(result.term_counts).length : 0}`);
            
            return result;
        } catch (error) {
            const elapsed = Date.now() - startTime;
            console.error(`❌ BACKEND OPERATION FAILED: Delete term pattern "${params.term_text}" after ${elapsed}ms`);
            throw error;
        }
    }
    
    /**
     * Modify term by editing actual claims text - persists through refreshes
     * @param {Object} params - Parameters {old_term, new_term, claims_text, document_id}
     */
    async modifyTermInText(params) {
        console.log(`🔄 BACKEND OPERATION STARTED: Modifying term in text "${params.old_term}" → "${params.new_term}"`);
        const startTime = Date.now();
        
        try {
            const response = await this.request('/modify-term-in-text', {
                method: 'PUT',
                body: JSON.stringify({
                    ...params,
                    document_id: this.getDocumentId()
                })
            });

            if (!response) return null; // Request was cancelled
            
            const result = await response.json();
            
            const elapsed = Date.now() - startTime;
            console.log(`✅ BACKEND OPERATION COMPLETED: Term modification "${params.old_term}" → "${params.new_term}" in ${elapsed}ms`);
            console.log(`   - Replacements made: ${result.text_changes?.replacements_made || 0}`);
            console.log(`   - Terms found: ${result.term_counts ? Object.keys(result.term_counts).length : 0}`);
            
            return result;
        } catch (error) {
            const elapsed = Date.now() - startTime;
            console.error(`❌ BACKEND OPERATION FAILED: Modify term "${params.old_term}" → "${params.new_term}" after ${elapsed}ms`);
            throw error;
        }
    }
    
    /**
     * Lightweight method to detect claim references only (no terms analysis)
     * @param {string} claims_text - Claims text to analyze
     */
    async detectClaimReferences(claims_text) {
        console.log(`🔄 BACKEND OPERATION STARTED: Detecting claim references`);
        console.log(`   - Text length: ${claims_text.length} characters`);
        const startTime = Date.now();
        
        try {
            const response = await this.request('/detect-references', {
                method: 'POST',
                body: JSON.stringify({ claims_text })
            });

            if (!response) return null; // Request was cancelled
            
            const result = await response.json();
            
            const elapsed = Date.now() - startTime;
            console.log(`✅ BACKEND OPERATION COMPLETED: Claim references detected in ${elapsed}ms`);
            console.log(`   - Claims found: ${result.claims ? Object.keys(result.claims).length : 0}`);
            console.log(`   - References: ${result.claim_refs ? Object.keys(result.claim_refs).length : 0}`);
            
            return result;
        } catch (error) {
            const elapsed = Date.now() - startTime;
            console.error(`❌ BACKEND OPERATION FAILED: Detect claim references after ${elapsed}ms`);
            throw error;
        }
    }
    
    /**
     * Validate claims structure without full analysis
     * @param {string} claims_text - Claims text to validate
     * @returns {Object} Object with validation_errors array
     */
    async validateClaims(claims_text) {
        console.log(`🔄 BACKEND OPERATION STARTED: Validating claims structure`);
        const startTime = Date.now();
        
        try {
            const response = await this.request('/validate-claims', {
                method: 'POST',
                body: JSON.stringify({ claims_text })
            });

            if (!response) return null; // Request was cancelled
            
            const result = await response.json();
            
            const elapsed = Date.now() - startTime;
            console.log(`✅ BACKEND OPERATION COMPLETED: Claims validation in ${elapsed}ms`);
            console.log(`   - Validation errors: ${result.validation_errors ? result.validation_errors.length : 0}`);
            if (result.validation_errors) {
                const blockingErrors = result.validation_errors.filter(e => e.blocking);
                console.log(`   - Blocking errors: ${blockingErrors.length}`);
            }
            
            return result;
        } catch (error) {
            const elapsed = Date.now() - startTime;
            console.error(`❌ BACKEND OPERATION FAILED: Validate claims after ${elapsed}ms`);
            throw error;
        }
    }
}

// Use environment variable for API URL, fallback to localhost for development
// This value is replaced at build time by rollup-plugin-replace
const API_URL = process.env.VITE_API_URL || 'http://localhost:5001';
export const backendService = new BackendService(API_URL);